import React, { useState } from 'react';
import { FileText, MapPin, FileCode } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { formatPeriodForCitation } from '../utils/formatPeriod';
import { getDocTypeBadgeClass } from '../utils/documentIcons';

interface CitationData {
  document_name: string;
  document_display_name?: string; // For display purposes
  page: number;
  paragraph?: number;
  text: string;
  document_type?: string;
  company_name?: string;
  document_period?: string;
  chunk_id?: string; // ID del chunk para buscar tablas asociadas
  document_id?: string; // ID del documento
}

interface CitationTooltipProps {
  citationData: CitationData;
  position: 'top' | 'bottom';
  onOpenPDF: () => void;
  onOpenChunk: () => void;
}

const CitationTooltip: React.FC<CitationTooltipProps> = ({
  citationData,
  position,
  onOpenPDF,
  onOpenChunk,
}) => {
  const { theme } = useTheme();

  // Mostrar solo un preview corto (150 caracteres)
  const maxInitialChars = 150;
  const displayText =
    citationData.text.length > maxInitialChars
      ? citationData.text.substring(0, maxInitialChars) + '...'
      : citationData.text;

  // Build document display name with metadata
  const getDocumentDisplayName = () => {
    if (citationData.company_name && citationData.document_type) {
      let name = `${citationData.company_name} - ${citationData.document_type}`;
      name += formatPeriodForCitation(citationData.document_period, citationData.document_type);
      return name;
    }
    return citationData.document_name;
  };

  // Build location string (page and paragraph)
  const getLocationString = () => {
    let location = `Página ${citationData.page}`;
    if (citationData.paragraph) {
      location += `, ¶${citationData.paragraph}`;
    }
    return location;
  };

  return (
    <div
      className={`${theme.colors.bg.input} border ${
        theme.colors.border.primary
      } rounded-lg shadow-xl overflow-hidden ${
        position === 'top' ? 'animate-fadeInUp' : 'animate-fadeInDown'
      }`}
      onClick={(e) => e.stopPropagation()}
      style={{
        width: '280px',
        maxHeight: '200px',
      }}
    >
      {/* Arrow */}
      <div
        className={`absolute left-1/2 transform -translate-x-1/2 w-3 h-3 ${theme.colors.bg.input} ${
          theme.colors.border.primary
        } ${
          position === 'top'
            ? 'bottom-[-6px] border-b border-r rotate-45'
            : 'top-[-6px] border-t border-l rotate-45'
        }`}
      />

      {/* Header compacto */}
      <div
        className={`${theme.colors.bg.primary} px-3 py-2 border-b ${theme.colors.border.primary}`}
      >
        <div className="flex items-start gap-2">
          <FileText className={`w-4 h-4 ${theme.colors.text.secondary} flex-shrink-0 mt-0.5`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className={`text-xs font-semibold ${theme.colors.text.primary} truncate`}>
                {getDocumentDisplayName()}
              </h3>
              {citationData.document_type && (
                <span
                  className={`text-[9px] font-semibold px-1 py-0.5 rounded flex-shrink-0 ${getDocTypeBadgeClass(
                    citationData.document_type
                  )}`}
                >
                  {citationData.document_type}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3 text-nb-error flex-shrink-0" />
              <p className={`text-[10px] ${theme.colors.text.secondary}`}>{getLocationString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content compacto */}
      <div className={`px-3 py-2 ${theme.colors.bg.input}`}>
        {/* Preview del texto */}
        <div
          className={`${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded p-2 mb-2`}
        >
          <div className={`text-[10px] ${theme.colors.text.primary} leading-relaxed line-clamp-3`}>
            {displayText}
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpenPDF();
            }}
            className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 ${theme.colors.bg.secondary} hover:${theme.colors.bg.tertiary} border ${theme.colors.border.primary} rounded text-[10px] font-medium ${theme.colors.text.primary} transition-colors`}
          >
            <FileText className="w-3 h-3" />
            Ver PDF
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpenChunk();
            }}
            className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 ${theme.colors.bg.secondary} hover:${theme.colors.bg.tertiary} border ${theme.colors.border.primary} rounded text-[10px] font-medium ${theme.colors.text.primary} transition-colors`}
          >
            <FileCode className="w-3 h-3" />
            Ver Chunk
          </button>
        </div>
      </div>
    </div>
  );
};

export default CitationTooltip;
